    'use client';
    import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
    import { authApi } from '@/lib/api/auth';
    import {
    ChangePasswordData,
    ForgotPasswordData,
    LoginCredentials,
    logOutAllDeviceData,
    ResetPasswordData,
    VerifyPasswordResetOtpData,
    } from '@/lib/types/auth.type';
    import { clearAllAuthData, hasAuthToken } from '@/utils/auth.utils';
    import { clearAuthCookies } from '@/app/actions/auth.actions';
    import { usePathname, useRouter } from 'next/navigation';
    import { isPublicRoute, isRegistrationRoute } from '@/lib/config/routes.config';
    import { useRef } from 'react';
    import { logger } from '@/utils/logger';

    export function useAuth() {
    const queryClient = useQueryClient();
    const router = useRouter();
    const pathname = usePathname();

    const isLoggingOutRef = useRef(false);

    // const shouldFetchUser = !isPublicRoute(pathname) && !isRegistrationRoute(pathname) && !isLoggingOutRef.current;
    const shouldFetchUser =
  !isPublicRoute(pathname) &&
  !isLoggingOutRef.current &&
  (isRegistrationRoute(pathname) ? hasAuthToken() : true);

    // Get User Data
    const {
        data: user,
        isLoading: isLoadingUser,
        refetch: refetchUser,
        error: userError,
    } = useQuery({
        queryKey: ['user'],
        queryFn: async () => {
        try {
            const response = await authApi.getCurrentUser();
            return response.data.data;
        } catch (error: any) {
            if (error.response?.status === 401) {
            return null;
            }
            throw error;
        }
        },
        enabled: shouldFetchUser,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        retry: false,
        staleTime: 5 * 60 * 1000,
    });

    const loginMutation = useMutation({
        mutationFn: (credentials: LoginCredentials) => authApi.login(credentials),
        onSuccess: (response) => {
        logger.log(' Login successful');
        //  Reset logout flag on login
        isLoggingOutRef.current = false;

        queryClient.invalidateQueries({ queryKey: ['user'] });
        queryClient.invalidateQueries({ queryKey: ['kyc-status'] });
        },
        onError: (error: any) => {
        logger.error('❌ Login failed:', error);
        },
    });

    // Logout mutation
    const logoutMutation = useMutation({
        mutationFn: async () => {
        //  Set flag BEFORE making logout request
        isLoggingOutRef.current = true;
        return authApi.logout();
        },
        onSuccess: async () => {
        clearAllAuthData();
        await clearAuthCookies();
        queryClient.clear();

        logger.log(' Logout successful');
        router.push('/login');
        },
        onError: async (error: any) => {
        logger.error('❌ Logout API failed:', error);

        clearAllAuthData();
        await clearAuthCookies();
        queryClient.clear();
        router.push('/login');
        },
        onSettled: () => {
        //  Reset flag after logout completes (success or error)
        setTimeout(() => {
            isLoggingOutRef.current = false;
        }, 1000);
        },
    });

    const handleLogout = () => {
        logoutMutation.mutate();
    };

    // Logout all devices
    const logOutAllDeviceMutation = useMutation({
        mutationFn: async (data: logOutAllDeviceData) => {
        //  Set flag before logout
        isLoggingOutRef.current = true;
        return authApi.logOutAllDevice(data.user_id);
        },
        onSuccess: async () => {
        clearAllAuthData();
        await clearAuthCookies();
        queryClient.clear();
        router.push('/login');
        },
        onError: async (error: any) => {
        logger.error('Logout all devices failed:', error);
        clearAllAuthData();
        await clearAuthCookies();
        queryClient.clear();
        router.push('/login');
        },
        onSettled: () => {
        setTimeout(() => {
            isLoggingOutRef.current = false;
        }, 1000);
        },
    });

    // Forgot password
    const forgotPasswordMutation = useMutation({
        mutationFn: (data: ForgotPasswordData) => authApi.forgotPassword(data),
        onError: (error: any) => {
        logger.error('Forgot password failed:', error);
        },
    });

    // Verify password reset OTP
    const verifyPasswordResetOtpMutation = useMutation({
        mutationFn: (data: VerifyPasswordResetOtpData) =>
        authApi.verifyPasswordResetOtp(data),
        onError: (error: any) => {
        logger.error('OTP verification failed:', error);
        },
    });

    // Reset password
    const resetPasswordMutation = useMutation({
        mutationFn: (data: ResetPasswordData) => authApi.resetPassword(data),
        onError: (error: any) => {
        logger.error('Password reset failed:', error);
        },
    });

    // Change password
    const changePasswordMutation = useMutation({
        mutationFn: (data: ChangePasswordData) => authApi.changePassword(data),
        onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: ['user'] });
        },
        onError: (error: any) => {
        logger.error('Change password failed:', error);
        },
    });

    return {
        // User data
        user,
        isLoadingUser,
        isAuthenticated: !!user,
        refetchUser,
        userError,

        // Login
        login: loginMutation.mutate,
        loginAsync: loginMutation.mutateAsync,
        isLoggingIn: loginMutation.isPending,
        loginError: loginMutation.error,
        loginData: loginMutation.data,

        // Logout
        logout: handleLogout,
        logoutWithCredentials: logoutMutation.mutate,
        isLoggingOut: logoutMutation.isPending,

        // Logout all devices
        logOutAllDevice: logOutAllDeviceMutation.mutate,
        isLoggingOutAllDevice: logOutAllDeviceMutation.isPending,
        logOutAllDeviceError: logOutAllDeviceMutation.error,
        logoutAllDeviceData: logOutAllDeviceMutation.data,

        // Password management
        forgotPassword: forgotPasswordMutation.mutate,
        isSendingResetCode: forgotPasswordMutation.isPending,
        forgotPasswordError: forgotPasswordMutation.error,
        forgotPasswordData: forgotPasswordMutation.data,

        verifyPasswordResetOtp: verifyPasswordResetOtpMutation.mutate,
        isVerifyingResetOtp: verifyPasswordResetOtpMutation.isPending,
        verifyPasswordResetOtpError: verifyPasswordResetOtpMutation.error,
        verifyPasswordResetOtpData: verifyPasswordResetOtpMutation.data,

        resetPassword: resetPasswordMutation.mutate,
        isResettingPassword: resetPasswordMutation.isPending,
        resetPasswordError: resetPasswordMutation.error,
        resetPasswordData: resetPasswordMutation.data,

        changePassword: changePasswordMutation.mutate,
        isChangingPassword: changePasswordMutation.isPending,
        changePasswordError: changePasswordMutation.error,
        changePasswordData: changePasswordMutation.data,
    };
    }
